<template>
    <div class="form-builder dark position-relative">
        <div v-if="error !== '' || success !== ''" class="alerts" role="status">
            <div v-if="error !== ''" class="alert alert-danger">
                {{ error }}
            </div>
            <div v-if="success !== ''" class="alert alert-success">
                {{ success }}
            </div>
        </div>
        <div class="row g-4">
            <div class="col-lg-7">
                <div v-if="restored" class="draft-notice">
                    <span>Picked up where you left off.</span>
                    <button
                        id="start_over"
                        class="btn btn-sm btn-outline-light"
                        @click.prevent="startOver"
                    >
                        Start over
                    </button>
                    <button
                        id="dismiss_restored"
                        class="draft-dismiss"
                        aria-label="Dismiss"
                        @click.prevent="restored = false"
                    >
                        <i class="fas fa-times" aria-hidden="true" />
                    </button>
                </div>
                <form
                    @input="createShortcode(form)"
                    @change="createShortcode(form)"
                >
                    <div class="row justify-content-center">
                        <div class="col-sm-2">
                            <div class="form-group">
                                <label>Tag:</label>
                                <input
                                    id="tag"
                                    name="tag"
                                    type="text"
                                    value="build-form"
                                    class="form-control"
                                    placeholder="Tag"
                                    v-model="form.tag"
                                />
                            </div>
                        </div>
                        <div class="col-sm-2">
                            <div class="form-group">
                                <label>Name:</label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value="build-form"
                                    class="form-control"
                                    placeholder="Name"
                                    v-model="form.name"
                                />
                            </div>
                        </div>
                        <div class="col-sm-2">
                            <div class="form-group">
                                <label>New Line:</label>
                                <input
                                    id="new_line"
                                    name="new_line"
                                    type="text"
                                    class="form-control"
                                    value=","
                                    v-model="form.new_line"
                                />
                            </div>
                        </div>
                        <div class="col-sm-2">
                            <div class="form-group">
                                <label>Delimiter:</label>
                                <input
                                    id="delimiter"
                                    name="delimiter"
                                    type="text"
                                    class="form-control"
                                    value="|"
                                    v-model="form.delimiter"
                                />
                            </div>
                        </div>
                        <div class="col-sm-3">
                            <div class="form-group">
                                <label>Submit Button Text:</label>
                                <input
                                    id="submit_button_name"
                                    name="submit_button_name"
                                    type="text"
                                    class="form-control"
                                    placeholder="text"
                                    value="Submit"
                                    v-model="form.submit_button_name"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="row justify-content-center">
                        <hr class="section-rule" />
                        <div class="col-sm-12 text-center">
                            <h2>Extras</h2>
                            <p class="section-note">
                                Any other attribute to put in the shortcode,
                                such as action or legend.
                            </p>
                        </div>
                        <div
                            v-for="(extra, index) in form.extras"
                            :key="index"
                            class="form-group row p-2 pe-5 rounded position-relative"
                            :class="index % 2 ? 'bg-dark' : ''"
                        >
                            <div class="col-sm-6">
                                <label>Heading:</label>
                                <input
                                    :id="'extra_label_' + index"
                                    name="heading"
                                    type="text"
                                    class="form-control"
                                    placeholder="heading"
                                    v-model="extra[0]"
                                />
                            </div>
                            <div class="col-sm-6">
                                <label>Value:</label>
                                <input
                                    :id="'extra_value_' + index"
                                    name="value"
                                    type="text"
                                    class="form-control"
                                    placeholder="value"
                                    v-model="extra[1]"
                                />
                            </div>
                            <button
                                :id="'remove_extra_' + index"
                                class="row-remove"
                                :disabled="form.extras.length < 2"
                                aria-label="Remove this extra"
                                @click.prevent="removeRow(form.extras, index)"
                            >
                                <i class="fas fa-times" aria-hidden="true" />
                            </button>
                        </div>
                        <div class="row mt-2">
                            <button
                                id="add_extra"
                                class="btn btn-dark col-12"
                                @click.prevent="addExtraRow"
                            >
                                <i class="fas fa-plus" aria-hidden="true" /> Add
                                extra
                            </button>
                        </div>
                        <div class="form-group row mt-3">
                            <div class="col-sm-12">
                                <label for="content">Description:</label>
                                <textarea
                                    id="content"
                                    name="content"
                                    class="form-control"
                                    rows="3"
                                    placeholder="Text shown above the form"
                                    v-model="form.content"
                                />
                            </div>
                        </div>
                    </div>
                    <hr class="section-rule" />
                    <div class="col-sm-12 text-center">
                        <h2>Questions</h2>
                        <p class="section-note">
                            The fields people fill in. This is what the form is
                            made of.
                        </p>
                    </div>
                    <div
                        v-for="(field, i) in form.questions"
                        :key="i"
                        class="row p-2 pe-5 rounded position-relative"
                        :class="i % 2 ? 'bg-dark' : ''"
                    >
                        <div v-if="fieldsFor(field[2]).label" class="col-sm-6">
                            <label>Label:</label>
                            <input
                                name="label"
                                type="text"
                                :id="'question_label_' + i"
                                class="form-control"
                                placeholder="Field Label"
                                v-model="field[0]"
                                @input="labelChanged(i, $event.target.value)"
                            />
                        </div>
                        <div v-if="fieldsFor(field[2]).name" class="col-sm-6">
                            <label>Name:</label>
                            <input
                                name="name"
                                type="text"
                                :id="'question_name_' + i"
                                class="form-control"
                                placeholder="Field Name"
                                v-model="field[1]"
                                @input="nameEdited[i] = true"
                            />
                        </div>
                        <div class="col-sm-6">
                            <label>Type:</label>
                            <select
                                name="type"
                                :id="'question_type_' + i"
                                class="form-control"
                                v-model="field[2]"
                            >
                                <option value="text">text</option>
                                <option value="email">email</option>
                                <option value="tel">tel</option>
                                <option value="date">date</option>
                                <option value="time">time</option>
                                <option value="password">password</option>
                                <option value="boolean">boolean</option>
                                <option value="file">file</option>
                                <option value="textarea">textarea</option>
                                <option value="hidden">hidden</option>
                                <option value="select">select</option>
                                <option value="recaptcha">recaptcha</option>
                                <option value="referred">referred</option>
                            </select>
                        </div>
                        <div v-if="fieldsFor(field[2]).value" class="col-sm-6">
                            <label>Value:</label>
                            <input
                                name="value"
                                type="text"
                                :id="'question_value_' + i"
                                class="form-control"
                                placeholder="Field Value"
                                v-model="field[3]"
                            />
                            <small
                                v-if="field[2] === 'select'"
                                class="form-text text-white-50"
                            >
                                Options separated by DL. Use NL to give an
                                option a different value and label:
                                <code>redNLRedDLblueNLBlue</code>
                            </small>
                        </div>
                        <div
                            v-if="fieldsFor(field[2]).comment"
                            class="col-sm-6"
                        >
                            <label>Comment:</label>

                            <input
                                name="comment"
                                type="text"
                                :id="'question_comment_' + i"
                                class="form-control"
                                placeholder="Field Comment"
                                v-model="field[4]"
                            />
                        </div>
                        <div
                            v-if="fieldsFor(field[2]).required"
                            class="col-sm-6"
                        >
                            <label>Required:</label>
                            <!-- Not v-model: a legacy row arrives with column
                                 5 null, which matches no option and would show
                                 blank. Coercing only the display lands it on
                                 the site default without writing an answer the
                                 author never gave. -->
                            <select
                                name="required"
                                :id="'question_required_' + i"
                                class="form-control"
                                :value="field[5] ?? ''"
                                @change="field[5] = $event.target.value"
                            >
                                <option value="required">Required</option>
                                <option value="optional">Optional</option>
                                <option value="">Use the site default</option>
                            </select>
                        </div>
                        <div
                            v-if="fieldsFor(field[2]).width"
                            class="col-sm-6"
                        >
                            <label>Width:</label>
                            <!-- Not v-model, for the same reason as Required:
                                 a legacy row arrives with column 6 null, which
                                 matches no option and would show blank. -->
                            <select
                                name="width"
                                :id="'question_width_' + i"
                                class="form-control"
                                :value="field[6] ?? ''"
                                @change="field[6] = $event.target.value"
                            >
                                <option value="1">Full width</option>
                                <option value="1/2">Half</option>
                                <option value="1/3">Third</option>
                                <option value="">Unset (full width)</option>
                            </select>
                        </div>
                        <div v-if="!fieldsFor(field[2]).name" class="col-sm-12">
                            <p class="section-note mb-0">
                                This type has no other settings.
                            </p>
                        </div>
                        <button
                            :id="'remove_question_' + i"
                            class="row-remove"
                            :disabled="form.questions.length < 2"
                            aria-label="Remove this question"
                            @click.prevent="removeQuestion(i)"
                        >
                            <i class="fas fa-times" aria-hidden="true" />
                        </button>
                    </div>
                    <div class="row mt-2">
                        <button
                            id="add_field_to_questions"
                            class="btn btn-dark col-12"
                            @click.prevent="addQuestion"
                        >
                            <i class="fas fa-plus" aria-hidden="true" /> Add
                            question
                        </button>
                    </div>
                    <hr class="section-rule" />
                    <div class="col-sm-12 text-center">
                        <h2>Headings</h2>
                        <p class="section-note">
                            A subtitle above a question. Name it after the
                            question it should sit above.
                        </p>
                    </div>
                    <div
                        v-for="(field, i) in form.headings"
                        :key="i"
                        class="row p-2 pe-5 rounded position-relative"
                        :class="i % 2 ? 'bg-dark' : ''"
                    >
                        <div class="col-sm-6">
                            <label>Heading:</label>
                            <input
                                name="label"
                                type="text"
                                :id="'heading_label_' + i"
                                class="form-control"
                                placeholder="Field Label"
                                v-model="field[0]"
                            />
                        </div>
                        <div class="col-sm-6">
                            <label>Element Name:</label>
                            <input
                                name="name"
                                type="text"
                                :id="'heading_name_' + i"
                                class="form-control"
                                placeholder="Field Name"
                                v-model="field[1]"
                            />
                        </div>
                        <button
                            :id="'remove_heading_' + i"
                            class="row-remove"
                            :disabled="form.headings.length < 2"
                            aria-label="Remove this heading"
                            @click.prevent="removeRow(form.headings, i)"
                        >
                            <i class="fas fa-times" aria-hidden="true" />
                        </button>
                    </div>
                    <div class="row mt-2">
                        <button
                            id="add_field_to_headings"
                            class="btn btn-dark col-12"
                            @click.prevent="addRow(form.headings, ['', ''])"
                        >
                            <i class="fas fa-plus" aria-hidden="true" /> Add
                            heading
                        </button>
                    </div>
                    <hr class="section-rule" />
                </form>
                <!--------------------------------------------------------------------------------------------------------------->
                <div class="mb-5">
                    <div
                        class="d-flex align-items-center justify-content-between mb-2"
                    >
                        <h2 class="mb-0">Shortcode</h2>
                        <button
                            id="copy"
                            class="btn btn-dark btn-sm"
                            @click.prevent="copyShortcode()"
                        >
                            <i class="fas fa-copy" aria-hidden="true" /> Copy
                            shortcode
                        </button>
                    </div>
                    <pre
                        class="shortcode-output"
                    ><code>{{ short_code }}</code></pre>
                    <input id="short_code" type="hidden" :value="short_code" />
                </div>

                <div class="row">
                    <div class="col-sm-12 mb-3">
                        <div class="form-group">
                            <label for="imported-short-code"
                                ><strong
                                    >Import an existing [short-code] to populate
                                    fields:</strong
                                ></label
                            >
                            <textarea
                                id="imported-short-code"
                                v-model="imported_short_code"
                                class="form-control"
                                rows="4"
                            />
                        </div>
                    </div>
                    <div class="col-sm-12">
                        <button
                            id="import"
                            class="btn btn-dark w-100"
                            @click.prevent="importShortcode()"
                        >
                            <i class="fas fa-upload" aria-hidden="true" />
                            Import
                        </button>
                    </div>
                </div>
            </div>
            <div class="col-lg-5">
                <div class="preview-pane">
                    <h2>Preview</h2>
                    <p class="section-note">
                        The form as it will render, updated as you build.
                    </p>
                    <iframe
                        id="preview"
                        name="preview"
                        class="preview-frame w-100"
                        title="Rendered form preview"
                        :src="previewUrl"
                    ></iframe>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";

const DRAFT_KEY = "form-builder:draft:v1";

const defaultForm = () => ({
    tag: "build-form",
    name: "build-form",
    new_line: ",",
    delimiter: "|",
    submit_button_name: "Submit",
    extras: [["", ""]],
    // Width defaults to empty, the opposite call from column 5. An unchecked
    // required control that emitted nothing would lie about the author's
    // answer, but an unset width has one obvious meaning, full width, and
    // defaulting to an explicit 1 would rewrite every builder-authored
    // shortcode for no gain.
    questions: [["", "", "text", "", "", "optional", ""]],
    headings: [["", ""]],
    ids: [],
    content: "",
});

// Anything coming back out of storage is untrusted: it can be from an older
// build, hand-edited, or truncated.
const isUsableDraft = (draft) => {
    const rows = (value) =>
        Array.isArray(value) && value.every((row) => Array.isArray(row));

    return (
        !!draft &&
        draft.version === 1 &&
        !!draft.form &&
        rows(draft.form.questions) &&
        draft.form.questions.length > 0 &&
        rows(draft.form.headings) &&
        rows(draft.form.extras)
    );
};

export default {
    name: "FormBuilder",
    data() {
        return {
            form: defaultForm(),
            error: "",
            success: "",
            short_code: "",
            imported_short_code: "",
            preview_short_code: "",
            preview_timer: null,
            message_timer: null,
            draft_timer: null,
            restored: false,
            // Per question row: has someone written the name by hand?
            nameEdited: [false],
        };
    },
    mounted() {
        const draft = this.readDraft();

        if (draft) {
            this.form = draft.form;
            this.nameEdited = Array.isArray(draft.nameEdited)
                ? draft.nameEdited
                : draft.form.questions.map(() => true);
            this.restored = true;
        }

        this.createShortcode(this.form);
    },
    computed: {
        previewUrl() {
            if (this.preview_short_code === "") {
                return "/form-template";
            }
            return `/form-template?shortcode=${encodeURIComponent(
                this.preview_short_code
            )}`;
        },
    },
    watch: {
        // The shortcode is rebuilt on every keystroke; only ask the server to
        // re-render once the typing pauses.
        // The form is the source of truth; the shortcode is rebuilt from it.
        form: {
            deep: true,
            handler() {
                clearTimeout(this.draft_timer);
                this.draft_timer = setTimeout(() => this.saveDraft(), 400);
            },
        },
        short_code: {
            immediate: true,
            handler(value) {
                clearTimeout(this.preview_timer);
                this.preview_timer = setTimeout(() => {
                    this.preview_short_code = value;
                }, 400);
            },
        },
    },
    methods: {
        createShortcode(event) {
            const vm = this;
            this.short_code = `[${event.tag} `;
            if (event.name) {
                this.short_code += `name="${event.name}" `;
            }
            if (event.new_line) {
                this.short_code += `new_line="${event.new_line}" `;
            }
            if (event.delimiter) {
                this.short_code += `delimiter="${event.delimiter}" `;
            }
            if (event.submit_button_name) {
                this.short_code += `submit_button_name="${event.submit_button_name}" `;
            }
            event.extras.forEach((extra) => {
                if (extra[0]) {
                    vm.short_code += `${extra[0]
                        .replace(/ /g, "_")
                        .toLowerCase()}="${extra[1]}" `;
                }
            });
            // One attribute holding every row. The parser keeps the last of a
            // repeated attribute, so emitting a prefix per row would throw
            // away every question but the final one.
            const row = (field, length) =>
                Array.from({ length }, (_, i) => {
                    const value = field[i];
                    if (!value) {
                        return "";
                    }
                    return i === 1
                        ? value.replace(/ /g, "-").toLowerCase()
                        : value;
                }).join(event.delimiter);

            vm.short_code += `questions="${event.questions
                .map((field) => row(field, 7))
                .join(event.new_line)}" `;

            vm.short_code += `headings="${event.headings
                .map((field) => row(field, 2))
                .join(event.new_line)}" `;

            // ids has no editor of its own; it round-trips from an import.
            // Not through row(): that lowercases column 1, and an id is
            // written back exactly as it came in.
            const ids = (event.ids ?? []).filter((field) => field[0]);
            if (ids.length) {
                vm.short_code += `ids="${ids
                    .map((field) => `${field[0]}${event.delimiter}${field[1] ?? ""}`)
                    .join(event.new_line)}" `;
            }

            const content = (event.content ?? "").trim();
            this.short_code += content ? `]${content}[/${event.tag}]` : "]";
        },
        addExtraRow() {
            this.addRow(this.form.extras, ["", ""]);
        },
        addQuestion() {
            this.nameEdited.push(false);
            this.addRow(this.form.questions, ["", "", "text", "", "", "optional", ""]);
        },
        removeQuestion(index) {
            if (this.form.questions.length < 2) {
                return;
            }
            this.nameEdited.splice(index, 1);
            this.removeRow(this.form.questions, index);
        },
        // A click is not an input event, so the shortcode has to be rebuilt
        // here or the change shows up only on the next keystroke.
        addRow(list, row) {
            list.push(row);
            this.createShortcode(this.form);
        },
        removeRow(list, index) {
            if (list.length < 2) {
                return;
            }
            list.splice(index, 1);
            this.createShortcode(this.form);
        },
        // The label fills the name in until someone writes one themselves.
        // Rewriting a hand-written field name would be worse than not helping.
        labelChanged(index, label) {
            if (this.nameEdited[index]) {
                return;
            }
            this.form.questions[index][1] = label
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "");
        },
        // Each type reads only part of a row. Showing the rest invites
        // filling in values the rendered form will ignore.
        fieldsFor(type) {
            if (type === "recaptcha" || type === "referred") {
                return {
                    label: false,
                    name: false,
                    value: false,
                    comment: false,
                    required: false,
                    width: false,
                };
            }
            if (type === "hidden") {
                return {
                    label: false,
                    name: true,
                    value: true,
                    comment: false,
                    required: false,
                    width: false,
                };
            }
            if (type === "boolean" || type === "file") {
                return {
                    label: true,
                    name: true,
                    value: false,
                    comment: true,
                    required: true,
                    width: true,
                };
            }

            return {
                label: true,
                name: true,
                value: true,
                comment: true,
                required: true,
                width: true,
            };
        },
        readDraft() {
            let raw = null;

            try {
                raw = window.localStorage.getItem(DRAFT_KEY);
            } catch (err) {
                // storage blocked, as in a private window: carry on without it
                return null;
            }

            if (!raw) {
                return null;
            }

            let draft = null;

            try {
                draft = JSON.parse(raw);
            } catch (err) {
                draft = null;
            }

            if (!isUsableDraft(draft)) {
                this.clearDraft();

                return null;
            }

            return draft;
        },
        saveDraft() {
            try {
                window.localStorage.setItem(
                    DRAFT_KEY,
                    JSON.stringify({
                        version: 1,
                        form: this.form,
                        nameEdited: this.nameEdited,
                    })
                );
            } catch (err) {
                // full or blocked: the builder still works, it just forgets
            }
        },
        clearDraft() {
            try {
                window.localStorage.removeItem(DRAFT_KEY);
            } catch (err) {
                // nothing worth doing
            }
        },
        startOver() {
            this.clearDraft();
            this.form = defaultForm();
            this.nameEdited = [false];
            this.restored = false;
            this.createShortcode(this.form);
        },
        flash(field, message) {
            this[field] = message;
            clearTimeout(this.message_timer);
            this.message_timer = setTimeout(() => {
                this.success = "";
                this.error = "";
            }, 5000);
        },
        copyShortcode() {
            const vm = this;
            const done = (ok) =>
                ok
                    ? vm.flash(
                          "success",
                          "[short-code] was copied successfully"
                      )
                    : vm.flash("error", "Oops, unable to copy");

            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard
                    .writeText(vm.short_code)
                    .then(() => done(true))
                    .catch(() => done(false));
                return;
            }

            done(vm.legacyCopy());
        },
        // navigator.clipboard does not exist outside a secure context.
        legacyCopy() {
            const input = document.querySelector("#short_code");
            input.setAttribute("type", "text");
            input.select();

            let copied = false;
            try {
                copied = document.execCommand("copy");
            } catch (err) {
                copied = false;
            }

            input.setAttribute("type", "hidden");
            window.getSelection().removeAllRanges();

            return copied;
        },
        importShortcode() {
            const vm = this;
            axios
                .post("/api/shortcode", {
                    shortcode: vm.imported_short_code,
                })
                .then((response) => {
                    if (
                        Object.prototype.hasOwnProperty.call(response, "error")
                    ) {
                        vm.flash("error", `Error: ${response.data.error}`);
                    } else {
                        vm.form = { ...response.data, content: response.data.content ?? "" };
                        vm.createShortcode(vm.form);
                        // Imported rows arrive named; the label must not start
                        // rewriting them.
                        vm.nameEdited = response.data.questions.map(() => true);
                        vm.flash(
                            "success",
                            "[short-code] was imported successfully"
                        );
                    }
                })
                .catch((e) => {
                    // A validation failure carries a response; a dropped
                    // connection does not. Reading straight through it threw
                    // inside the handler and left the failure invisible.
                    vm.flash(
                        "error",
                        e.response?.data?.errors?.shortcode?.[0] ??
                            e.response?.data?.error ??
                            `Import failed: ${e.message}`
                    );
                });
        },
    },
};
</script>

<style scoped>
.form-builder .alerts {
    bottom: 1rem;
    max-width: 24rem;
    /* the alerts used to sit over the buttons at the foot of the page and
       swallow clicks meant for them */
    pointer-events: none;
    position: fixed;
    right: 1rem;
    width: calc(100% - 2rem);
    z-index: 1080;
}

.form-builder .section-note {
    color: #94a3b8;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
    /* centred sections centre their note with it */
    margin-left: auto;
    margin-right: auto;
    max-width: 60ch;
}

.shortcode-output {
    background: #0f1319;
    border: 1px solid #2c3445;
    border-radius: 8px;
    color: #cbd5e1;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.85rem;
    line-height: 1.6;
    margin: 0;
    max-height: 12rem;
    overflow: auto;
    padding: 12px 14px;
    white-space: pre-wrap;
    word-break: break-word;
}

@media (min-width: 992px) {
    .preview-pane {
        position: sticky;
        top: 1rem;
    }

    .preview-frame {
        min-height: 70vh;
    }
}

.preview-frame {
    background: #ffffff;
    border: 1px solid #2c3445;
    border-radius: 8px;
    min-height: 420px;
}

.form-builder {
    background: #121417;
    color: #e5e7eb;
    padding: 24px;
    border-radius: 12px;
}

.form-builder h1,
.form-builder h2,
.form-builder h5,
.form-builder label {
    color: #e5e7eb;
}

.form-builder p {
    color: #cbd5e1;
}

.form-builder .form-control,
.form-builder select.form-control,
.form-builder textarea.form-control {
    background-color: #1a1f2b;
    border-color: #2c3445;
    color: #e5e7eb;
}

.form-builder .form-control:focus,
.form-builder select.form-control:focus,
.form-builder textarea.form-control:focus {
    background-color: #1d2432;
    border-color: #3b82f6;
    box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
    color: #f3f4f6;
}

.form-builder .form-control::placeholder {
    color: #94a3b8;
}

.form-builder .btn {
    border-radius: 8px;
}

/* Accentuate the existing dark button to a nicer blue on dark surfaces */
.form-builder .btn.btn-dark {
    background-color: #3b82f6;
    border-color: #3b82f6;
}
.form-builder .btn.btn-dark:hover,
.form-builder .btn.btn-dark:focus {
    background-color: #2563eb;
    border-color: #2563eb;
}

/* Links (used around +/- icons) */
.form-builder a {
    color: #93c5fd;
}
.form-builder a:hover {
    color: #bfdbfe;
}

/* Alerts on dark */
.form-builder .alert.alert-danger {
    background-color: #2a1b1b;
    color: #fecaca;
    border-color: #7f1d1d;
}
.form-builder .alert.alert-success {
    background-color: #1b2a1b;
    color: #bbf7d0;
    border-color: #14532d;
}

.form-builder .draft-notice {
    align-items: center;
    background: #16233a;
    border: 1px solid #2c3445;
    border-radius: 8px;
    color: #cbd5e1;
    display: flex;
    font-size: 0.9rem;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding: 0.6rem 0.9rem;
}

.form-builder .draft-notice span {
    flex: 1;
}

.form-builder .draft-dismiss {
    background: none;
    border: 0;
    color: #94a3b8;
    line-height: 1;
    padding: 0;
}

.form-builder .draft-dismiss:hover {
    color: #fecaca;
}

.form-builder .draft-dismiss:focus-visible {
    color: #fecaca;
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
}

.form-builder .draft-notice i.fas {
    font-size: 1rem;
    margin: 0;
}

/* The site's .divider-slash is a brand device sized for a light page; here it
   just shouts. app.css is shared with the rendered forms, so this replaces the
   class rather than restyling it. */
.form-builder .section-rule {
    border: 0;
    border-top: 1px solid #2c3445;
    margin: 2.5rem 0 1.75rem;
    opacity: 1;
}

/* Sits in the gutter the row reserves for it rather than on a line of its own. */
.form-builder .row-remove {
    background: none;
    border: 0;
    color: #94a3b8;
    line-height: 1;
    padding: 0;
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
    /* bootstrap gives every direct child of .row width:100%, which stretches
       the button into a bar across the row */
    max-width: none;
    width: auto;
    z-index: 1;
}

/* the icon rule below adds 6px either side, which is most of the bulk */
.form-builder .row-remove i.fas {
    font-size: 1rem;
    margin: 0;
}

.form-builder .row-remove:hover:not(:disabled) {
    color: #fecaca;
}

/* nothing but colour marks this control now, so keep a real focus ring */
.form-builder .row-remove:focus-visible {
    color: #fecaca;
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
}

.form-builder .row-remove:disabled {
    opacity: 0.35;
}

/* Fine-tune spacing for icon-only links */
.form-builder i.fas {
    font-size: 1.25rem;
    vertical-align: middle;
    margin-left: 6px;
    margin-right: 6px;
}
</style>
